import type { ViewDefinitionV1 } from "@/shared/domain/views";

import {
  viewStateToDefinition,
  type ResourceViewState,
} from "../viewSerialization";

/**
 * Has the resource view drifted from the View it loaded?
 *
 * Derived by re-serializing and comparing rather than tracked with a flag: a
 * flag has to be cleared on every path back to the saved state, and each missed
 * path claims changes that do not exist.
 *
 * Columns are compared only when the View pinned an explicit list —
 * `visible_columns: []` means no preference, and comparing it against the
 * table's defaults would mark every such View dirty on load. Order matters.
 */
export function isViewDirty(
  saved: ViewDefinitionV1,
  current: ResourceViewState,
): boolean {
  const live = viewStateToDefinition(current);

  if (saved.roboql !== live.roboql) {
    return true;
  }

  if (!sameFilters(saved, live)) {
    return true;
  }

  if (
    saved.display.sort_by !== live.display.sort_by ||
    saved.display.sort_direction !== live.display.sort_direction ||
    saved.display.page_size !== live.display.page_size
  ) {
    return true;
  }

  if (saved.display.visible_columns.length > 0) {
    return !sameOrder(
      saved.display.visible_columns,
      live.display.visible_columns,
    );
  }

  return false;
}

function sameFilters(a: ViewDefinitionV1, b: ViewDefinitionV1): boolean {
  if (a.filters === null || b.filters === null) {
    return a.filters === b.filters;
  }
  if (a.filters.match_mode !== b.filters.match_mode) {
    return false;
  }
  // Key-order-insensitive, because the two sides disagree on it: the frontend
  // emits `{type, field, ...}` and Pydantic emits `{field, type, ...}` (base
  // class fields first). Plain `JSON.stringify` made every saved View read
  // dirty on load.
  return (
    stableStringify(a.filters.filters) === stableStringify(b.filters.filters)
  );
}

/**
 * JSON with object keys sorted, so two encodings of the same value compare
 * equal. Arrays keep their order — a filter list is an order the author chose.
 */
function stableStringify(value: unknown): string {
  if (Array.isArray(value)) {
    return `[${value.map(stableStringify).join(",")}]`;
  }

  if (typeof value === "object" && value !== null) {
    const entries = Object.entries(value)
      .sort(([left], [right]) => left.localeCompare(right))
      .map(
        ([key, nested]) => `${JSON.stringify(key)}:${stableStringify(nested)}`,
      );
    return `{${entries.join(",")}}`;
  }

  // `undefined` has no JSON encoding, and would otherwise poison the string.
  return JSON.stringify(value) ?? "null";
}

function sameOrder(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((value, i) => value === b[i]);
}
